// Shopify integration service for connection status and product import

export interface ShopifyConnectionStatus {
  connected: boolean;
  shopDomain?: string;
  lastSync?: string;
}

export interface ShopifyProduct {
  id: string;
  title: string;
  description: string;
  price: number;
  compareAtPrice?: number;
  images: string[];
  variants: ShopifyVariant[];
  tags: string[];
  vendor: string;
}

export interface ShopifyVariant {
  id: string;
  title: string;
  price: number;
  sku: string;
  inventoryQuantity: number;
  weight: number;
  weightUnit: string;
}

export interface ProductImportConfig {
  shopifyProductId: string;
  title: string;
  description: string;
  price: number;
  compareAtPrice?: number;
  images: string[];
  selectedVariants: string[];
  commission: number;
  margin: number;
  category: string;
  tags: string[];
}

// Mock Shopify connection status
export async function checkShopifyConnection(): Promise<ShopifyConnectionStatus> {
  // Simulate API call
  await new Promise((resolve) => setTimeout(resolve, 500));

  // Check localStorage for connection status
  const connected = localStorage.getItem("shopify_connected") === "true";

  if (connected) {
    return {
      connected: true,
      shopDomain: "stali.myshopify.com",
      lastSync: new Date().toISOString(),
    };
  }

  return { connected: false };
}

// Mock Shopify products fetch
export async function fetchShopifyProducts(): Promise<ShopifyProduct[]> {
  // Simulate API call
  await new Promise((resolve) => setTimeout(resolve, 800));

  return [
    {
      id: "shopify_1",
      title: "Italo Disco Italian Espresso Blend",
      description:
        "A vibrant Italian-style espresso with notes of dark chocolate and spice",
      price: 20.0,
      compareAtPrice: 24.0,
      images: ["/coffee-bag-italian.jpg"],
      variants: [
        {
          id: "var_1",
          title: "250g",
          price: 20.0,
          sku: "ITALO-250",
          inventoryQuantity: 150,
          weight: 250,
          weightUnit: "g",
        },
        {
          id: "var_2",
          title: "1kg",
          price: 72.0,
          sku: "ITALO-1KG",
          inventoryQuantity: 50,
          weight: 1000,
          weightUnit: "g",
        },
      ],
      tags: ["Dark Chocolate", "Spice", "Espresso"],
      vendor: "ST. ALi",
    },
    {
      id: "shopify_2",
      title: "Orthodox House Espresso Blend",
      description:
        "Our signature house blend with chocolate, fudge, and jammy notes",
      price: 20.0,
      images: ["/coffee-bag-orthodox.jpg"],
      variants: [
        {
          id: "var_3",
          title: "250g",
          price: 20.0,
          sku: "ORTHO-250",
          inventoryQuantity: 200,
          weight: 250,
          weightUnit: "g",
        },
        {
          id: "var_4",
          title: "1kg",
          price: 72.0,
          sku: "ORTHO-1KG",
          inventoryQuantity: 75,
          weight: 1000,
          weightUnit: "g",
        },
      ],
      tags: ["Chocolate", "Fudge", "Jammy", "Espresso"],
      vendor: "ST. ALi",
    },
    {
      id: "shopify_3",
      title: "Wide Awake Dark Espresso Blend",
      description:
        "Bold dark roast with butterscotch, chocolate, and praline flavors",
      price: 20.0,
      images: ["/coffee-bag-dark.jpg"],
      variants: [
        {
          id: "var_5",
          title: "250g",
          price: 20.0,
          sku: "WIDE-250",
          inventoryQuantity: 180,
          weight: 250,
          weightUnit: "g",
        },
      ],
      tags: ["Butterscotch", "Chocolate", "Praline", "Dark Roast"],
      vendor: "ST. ALi",
    },
    {
      id: "shopify_4",
      title: "Feels Good Organic Espresso Blend",
      description:
        "Certified organic blend with red apple and maple syrup notes",
      price: 22.0,
      images: ["/coffee-bag-organic.jpg"],
      variants: [
        {
          id: "var_6",
          title: "250g",
          price: 22.0,
          sku: "FEELS-250",
          inventoryQuantity: 120,
          weight: 250,
          weightUnit: "g",
        },
      ],
      tags: ["Red Apple", "Maple Syrup", "Organic", "Espresso"],
      vendor: "ST. ALi",
    },
  ];
}

// Mock product import
export async function importProduct(): Promise<{
  success: boolean;
  productId?: string;
  error?: string;
}> {
  // Simulate API call
  await new Promise((resolve) => setTimeout(resolve, 1000));

  // Simulate 95% success rate
  if (Math.random() > 0.05) {
    return {
      success: true,
      productId: `prod_${Date.now()}`,
    };
  }

  return {
    success: false,
    error: "Failed to import product. Please try again.",
  };
}

// Connect to Shopify
export async function connectShopify(
  shopDomain: string,
): Promise<{ success: boolean; error?: string }> {
  // Simulate API call
  await new Promise((resolve) => setTimeout(resolve, 1500));

  if (shopDomain.includes(".myshopify.com")) {
    localStorage.setItem("shopify_connected", "true");
    localStorage.setItem("shopify_domain", shopDomain);
    return { success: true };
  }

  return {
    success: false,
    error: "Invalid Shopify domain. Please use format: yourstore.myshopify.com",
  };
}
